import { useState, useRef, useEffect } from 'react'
import { t } from '@copy/index'
import { useLock } from '@contexts/index'

export function NeutralScreen() {
  const { reveal } = useLock()
  const [scratch, setScratch] = useState('')
  const [history, setHistory] = useState<string[]>([])
  const [historyIndex, setHistoryIndex] = useState(-1)
  const [fontSize, setFontSize] = useState(14)
  const [category, setCategory] = useState('personal')
  const [syncState, setSyncState] = useState<'synced' | 'syncing'>('synced')
  const [toast, setToast] = useState<string | null>(null)
  
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  // Auto-sync visual effect when typing
  const triggerSyncEffect = () => {
    setSyncState('syncing')
    if (syncTimeoutRef.current !== undefined) {
      clearTimeout(syncTimeoutRef.current)
    }
    syncTimeoutRef.current = setTimeout(() => {
      setSyncState('synced')
    }, 1200)
  }

  // Toast message helper
  const showToast = (message: string) => {
    setToast(message)
    if (toastTimeoutRef.current !== undefined) {
      clearTimeout(toastTimeoutRef.current)
    }
    toastTimeoutRef.current = setTimeout(() => {
      setToast(null)
    }, 2000)
  }

  // Handle typing changes
  const handleTextChange = (value: string) => {
    setScratch(value)
    triggerSyncEffect()

    // Add to undo/redo history (throttled)
    const newHistory = history.slice(0, historyIndex + 1)
    if (newHistory[newHistory.length - 1] !== value) {
      newHistory.push(value)
      // Limit history size to 50
      if (newHistory.length > 50) {
        newHistory.shift()
      }
      setHistory(newHistory)
      setHistoryIndex(newHistory.length - 1)
    }
  }

  // Initialize history
  useEffect(() => {
    setHistory([''])
    setHistoryIndex(0)
  }, [])

  // Clear timers on unmount
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current !== undefined) clearTimeout(syncTimeoutRef.current)
      if (toastTimeoutRef.current !== undefined) clearTimeout(toastTimeoutRef.current)
    }
  }, [])

  // Listen for Escape key to close the decoy screen and return to Thana
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        reveal()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [reveal])


  // Undo/Redo logic
  const handleUndo = () => {
    if (historyIndex > 0) {
      const nextIndex = historyIndex - 1
      setHistoryIndex(nextIndex)
      setScratch(history[nextIndex] ?? '')
      triggerSyncEffect()
    }
  }

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const nextIndex = historyIndex + 1
      setHistoryIndex(nextIndex)
      setScratch(history[nextIndex] ?? '')
      triggerSyncEffect()
    }
  }


  // Copy to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(scratch)
      showToast(t('neutral.copied'))
    } catch {
      showToast('Failed to copy')
    }
  }

  // Apply markdown format wrapper
  const wrapText = (before: string, after: string = '') => {
    const el = textareaRef.current
    if (!el) return

    const start = el.selectionStart
    const end = el.selectionEnd
    const selection = scratch.substring(start, end)
    const wrapped = before + selection + (after || before)
    const nextText = scratch.substring(0, start) + wrapped + scratch.substring(end)

    handleTextChange(nextText)
    
    // Restore selection focus
    setTimeout(() => {
      el.focus()
      el.setSelectionRange(start + before.length, start + before.length + selection.length)
    }, 10)
  }

  // Count text stats
  const charCount = scratch.length
  const wordCount = scratch.trim() === '' ? 0 : scratch.trim().split(/\s+/).length
  const readTime = Math.ceil(wordCount / 200)

  return (
    <main className="flex h-full flex-col bg-[var(--color-surface)] text-[var(--color-ink)] p-4 font-sans select-none">
      {/* Decoy Header */}
      <header className="flex items-center justify-between border-b border-line pb-3 mb-3">
        <div className="flex items-center gap-2">
          {/* Decoy Category Selector */}
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="text-sm font-semibold bg-surface-raised border border-line rounded px-2 py-1 select-none focus:outline-none focus:border-accent"
          >
            <option value="personal">{t('neutral.catPersonal')}</option>
            <option value="work">{t('neutral.catWork')}</option>
            <option value="ideas">{t('neutral.catIdeas')}</option>
            <option value="lists">{t('neutral.catLists')}</option>
          </select>
        </div>

        {/* Sync Status Badge */}
        <div className="flex items-center gap-1.5 text-xs text-ink-muted">
          {syncState === 'syncing' ? (
            <>
              {/* Spinner */}
              <svg className="animate-spin h-3.5 w-3.5 text-accent" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span>{t('neutral.syncing')}</span>
            </>
          ) : (
            <>
              {/* Checkmark */}
              <svg className="h-3.5 w-3.5 text-emerald-500" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              <span>{t('neutral.synced')}</span>
            </>
          )}
        </div>
      </header>

      {/* Interactive Toolbar */}
      <section className="flex flex-wrap items-center gap-1 bg-surface-raised border border-line rounded-t-lg p-2 gap-y-2">
        {/* Formatting Actions */}
        <div className="flex items-center gap-0.5 border-r border-line pr-1.5 mr-1">
          <button
            type="button"
            onClick={() => wrapText('**')}
            className="h-8 w-8 flex items-center justify-center rounded text-sm font-bold hover:bg-line/40 transition-colors"
            title="Bold"
          >
            B
          </button>
          <button
            type="button"
            onClick={() => wrapText('*')}
            className="h-8 w-8 flex items-center justify-center rounded text-sm italic hover:bg-line/40 transition-colors"
            title="Italic"
          >
            I
          </button>
          <button
            type="button"
            onClick={() => wrapText('<u>', '</u>')}
            className="h-8 w-8 flex items-center justify-center rounded text-sm underline hover:bg-line/40 transition-colors"
            title="Underline"
          >
            U
          </button>
          <button
            type="button"
            onClick={() => wrapText('~~')}
            className="h-8 w-8 flex items-center justify-center rounded text-sm line-through hover:bg-line/40 transition-colors"
            title="Strikethrough"
          >
            S
          </button>
          <button
            type="button"
            onClick={() => wrapText('`')}
            className="h-8 w-8 flex items-center justify-center rounded text-xs font-mono hover:bg-line/40 transition-colors"
            title="Code"
          >
            &lt;/&gt;
          </button>
          <button
            type="button"
            onClick={() => wrapText('\n- ')}
            className="h-8 w-8 flex items-center justify-center rounded hover:bg-line/40 transition-colors"
            title="Bullet List"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12M8.25 17.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
            </svg>
          </button>
        </div>

        {/* History / Clipboard Actions */}
        <div className="flex items-center gap-0.5 border-r border-line pr-1.5 mr-1">
          <button
            type="button"
            onClick={handleUndo}
            disabled={historyIndex <= 0}
            className="h-8 w-8 flex items-center justify-center rounded hover:bg-line/40 transition-colors disabled:opacity-40"
            title="Undo"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
            </svg>
          </button>
          <button
            type="button"
            onClick={handleRedo}
            disabled={historyIndex >= history.length - 1}
            className="h-8 w-8 flex items-center justify-center rounded hover:bg-line/40 transition-colors disabled:opacity-40"
            title="Redo"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 15l6-6m0 0l-6-6m6 6H9a6 6 0 000 12h3" />
            </svg>
          </button>
          <button
            type="button"
            onClick={handleCopy}
            className="h-8 w-8 flex items-center justify-center rounded hover:bg-line/40 transition-colors"
            title="Copy Note"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376A8.965 8.965 0 0012 12.75a8.965 8.965 0 00-3.75-2.25m9 7.25h1.5a1.125 1.125 0 001.125-1.125V2.25z" />
            </svg>
          </button>
        </div>

        {/* Dynamic Font Size Control */}
        <div className="flex items-center gap-2 text-xs text-ink-muted">
          <span>A</span>
          <input
            type="range"
            min="12"
            max="22"
            value={fontSize}
            onChange={(e) => setFontSize(Number(e.target.value))}
            className="w-20 accent-accent cursor-pointer"
            title={t('neutral.sizeLabel')}
          />
          <span className="text-sm font-semibold">A</span>
        </div>
      </section>

      {/* Editor Body */}
      <div className="flex-1 min-h-0 relative">
        <textarea
          ref={textareaRef}
          aria-label={t('neutral.label')}
          placeholder={t('neutral.placeholder')}
          value={scratch}
          onChange={(event) => handleTextChange(event.target.value)}
          style={{ fontSize: `${fontSize}px` }}
          className="w-full h-full resize-none rounded-b-lg border-x border-b border-line bg-surface-raised p-3 text-sm focus:outline-none focus:border-accent"
        />
      </div>

      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 bg-ink text-surface-raised text-xs px-3 py-2 rounded-lg shadow-lg flex items-center gap-1.5 animate-scale-bounce">
          <svg className="h-3.5 w-3.5 text-emerald-500" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{toast}</span>
        </div>
      )}

      {/* Bottom Status Bar */}
      <footer className="flex items-center justify-between mt-3 text-xs text-ink-muted">
        {/* live text stats */}
        <div className="flex items-center gap-2">
          <span>
            {wordCount} {t('neutral.words')}
          </span>
          <span>•</span>
          <span>
            {charCount} {t('neutral.chars')}
          </span>
          <span>•</span>
          <span>
            {readTime} {t('neutral.readTime')}
          </span>
        </div>

        {/* Back navigation */}
        <button
          type="button"
          onClick={reveal}
          className="min-h-9 px-3 py-1.5 rounded-lg border border-line bg-surface-raised font-medium hover:bg-line/40 hover:text-ink active:scale-95 transition-all"
        >
          {t('neutral.back')}
        </button>
      </footer>
    </main>
  )
}
